import { duckTarget, detectSpeaking, shouldDuck, DEFAULT_DUCK, type DuckConfig } from "./audio.js";

interface Voice {
  source: MediaStreamAudioSourceNode;
  analyser: AnalyserNode;
  buf: Uint8Array<ArrayBuffer>;
  speaking: boolean;
}

/**
 * Web Audio graph for the watch session (PRD §6):
 *   movie  → movieGain → destination
 *   voices → voiceGain → destination
 * `movieGain` carries the user's movie-volume slider AND the auto-duck; voices
 * carry the user's voice-volume slider. Speaking is detected from each voice's
 * analyser and ducks the movie with a hold window so it doesn't pump.
 *
 * The decision functions (`duckTarget`, `shouldDuck`, `detectSpeaking`) are pure
 * and unit-tested in audio.test.ts; this class is the plumbing around them.
 */
export class AudioMixer {
  readonly ctx: AudioContext;
  private readonly movieGain: GainNode;
  private readonly voiceGain: GainNode;
  private readonly voices = new Map<string, Voice>();

  private userMovieVol = 1;
  private userVoiceVol = 1;
  private lastSpeechMs: number | null = null;
  private poller: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly duck: DuckConfig = DEFAULT_DUCK, private readonly now: () => number = () => Date.now()) {
    this.ctx = new AudioContext();
    this.movieGain = this.ctx.createGain();
    this.voiceGain = this.ctx.createGain();
    this.movieGain.connect(this.ctx.destination);
    this.voiceGain.connect(this.ctx.destination);
    this.applyMovieGain(false);
    this.voiceGain.gain.value = this.userVoiceVol;
  }

  /** Resume the context — call inside the Join user gesture (audio unlock). */
  resume(): Promise<void> {
    return this.ctx.resume();
  }

  get state(): AudioContextState {
    return this.ctx.state;
  }

  /** Route the movie element's audio through the duckable movie bus. */
  connectMovie(el: HTMLMediaElement): void {
    const src = this.ctx.createMediaElementSource(el);
    src.connect(this.movieGain);
  }

  addVoice(peerId: string, stream: MediaStream): void {
    if (this.voices.has(peerId)) this.removeVoice(peerId);
    const source = this.ctx.createMediaStreamSource(stream);
    const analyser = this.ctx.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    source.connect(this.voiceGain);
    this.voices.set(peerId, {
      source,
      analyser,
      buf: new Uint8Array(analyser.fftSize),
      speaking: false,
    });
    this.ensurePolling();
  }

  removeVoice(peerId: string): void {
    const v = this.voices.get(peerId);
    if (!v) return;
    try {
      v.source.disconnect();
    } catch {
      /* already disconnected */
    }
    this.voices.delete(peerId);
    if (this.voices.size === 0) this.stopPolling();
  }

  setMovieVolume(v: number): void {
    this.userMovieVol = clamp01(v);
    this.applyMovieGain(this.isDucked());
  }

  setVoiceVolume(v: number): void {
    this.userVoiceVol = clamp01(v);
    this.voiceGain.gain.value = this.userVoiceVol;
  }

  /** Exposed for the e2e duck proxy. */
  get movieGainValue(): number {
    return this.movieGain.gain.value;
  }

  private isDucked(): boolean {
    return shouldDuck(this.lastSpeechMs, this.now(), this.duck);
  }

  private applyMovieGain(ducked: boolean): void {
    const target = duckTarget(ducked, this.duck) * this.userMovieVol;
    const t = this.ctx.currentTime;
    const ramp = (ducked ? this.duck.attackMs : this.duck.releaseMs) / 1000;
    this.movieGain.gain.cancelScheduledValues(t);
    this.movieGain.gain.setTargetAtTime(target, t, Math.max(0.01, ramp / 3));
  }

  /** Public hook so a remote-speaking signal (or e2e) can drive the duck directly. */
  noteSpeech(): void {
    this.lastSpeechMs = this.now();
    this.applyMovieGain(true);
  }

  private ensurePolling(): void {
    if (this.poller) return;
    this.poller = setInterval(() => this.pollSpeaking(), 100);
  }

  private stopPolling(): void {
    if (this.poller) clearInterval(this.poller);
    this.poller = null;
  }

  private pollSpeaking(): void {
    let any = false;
    for (const v of this.voices.values()) {
      v.analyser.getByteTimeDomainData(v.buf);
      let sumSq = 0;
      for (let i = 0; i < v.buf.length; i++) {
        const x = (v.buf[i]! - 128) / 128;
        sumSq += x * x;
      }
      const rms = Math.sqrt(sumSq / v.buf.length);
      v.speaking = detectSpeaking(rms, v.speaking);
      if (v.speaking) any = true;
    }
    if (any) this.lastSpeechMs = this.now();
    this.applyMovieGain(this.isDucked());
  }

  destroy(): void {
    this.stopPolling();
    void this.ctx.close();
  }
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}
